import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, Camera, Clock, CheckCircle2, Ban } from 'lucide-react';
import { API } from '../lib/constants';
import { PublicShell } from '../components/public/PublicShell';
import { LoadingScreen } from '../components/LoadingScreen';

const fmtRemaining = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
};

const PhotographerUploadPage = () => {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);

  const load = useCallback(() => axios.get(`${API}/upload/${token}`).then((r) => setLink(r.data)).catch((e) => setError(e.response?.data?.detail || 'Upload link not found')), [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/upload/${token}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setSessionCount((c) => c + data.uploaded_count);
      toast.success(`${data.uploaded_count} photo(s) uploaded to the event folder`);
      setFiles([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      if (e.response?.status === 410) load();
    } finally {
      setUploading(false);
    }
  };

  if (error) {
    return (
      <PublicShell tenant={null}>
        <div className="card-brutal p-10 text-center max-w-md mx-auto" data-testid="upload-link-error">
          <span className="w-12 h-12 bg-panel border-2 border-line text-slate-400 flex items-center justify-center mx-auto mb-5"><Ban className="w-6 h-6" /></span>
          <h1 className="h-display text-2xl">Upload link unavailable</h1>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
        </div>
      </PublicShell>
    );
  }
  if (!link) return <LoadingScreen label="Loading upload link…" />;

  const active = link.status === 'active';
  const tenant = link.tenant ? { ...link.tenant, branding: { hide_branding: true } } : null;

  return (
    <PublicShell tenant={tenant}>
      <div className="mb-8 fade-up">
        <p className="eyebrow mb-3">Photographer upload</p>
        <h1 className="h-display text-4xl sm:text-5xl leading-[0.95]" data-testid="upload-page-title">{link.tenant?.name}</h1>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="badge-cyan" data-testid="upload-photographer-name"><Camera className="w-3 h-3" /> {link.photographer_name}</span>
          <span className={active ? 'badge-lime' : 'badge-danger'} data-testid="upload-link-status">
            <Clock className="w-3 h-3" /> {active ? fmtRemaining(link.expires_at) : link.status}
          </span>
          <span className="badge-muted" data-testid="upload-link-count">{link.photo_count} photos uploaded via this link</span>
        </div>
      </div>

      {active ? (
        <div className="card-brutal p-6 fade-up fade-up-delay-1" data-testid="photographer-upload-card">
          <label htmlFor="ph-file-input" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-line p-12 bg-ink hover:border-cyan cursor-pointer transition-colors text-center">
            <Upload className="w-7 h-7 text-cyan" />
            <p className="font-display font-bold uppercase tracking-wider text-sm text-white">
              {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} ready` : 'Drop your card here'}
            </p>
            <p className="font-mono text-[11px] text-slate-500">JPG · PNG · up to 50 per batch · uploads straight to the event folder</p>
            <input id="ph-file-input" data-testid="photographer-file-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
          </label>
          {uploading && (
            <div className="mt-4 h-2 bg-ink border border-line"><div className="h-full bg-cyan transition-all" style={{ width: `${progress}%` }} /></div>
          )}
          <button data-testid="photographer-upload-btn" onClick={upload} disabled={uploading || files.length === 0} className="btn-primary w-full mt-5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? `Uploading ${progress}%` : `Upload ${files.length || ''} photo${files.length === 1 ? '' : 's'}`}
          </button>
          {sessionCount > 0 && (
            <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-lime" data-testid="session-upload-count">
              <CheckCircle2 className="w-3.5 h-3.5" /> {sessionCount} uploaded this session
            </p>
          )}
        </div>
      ) : (
        <div className="card p-10 text-center max-w-md mx-auto" data-testid="upload-link-inactive">
          <span className="w-12 h-12 bg-panel border-2 border-line text-slate-400 flex items-center justify-center mx-auto mb-5"><Ban className="w-6 h-6" /></span>
          <h2 className="h-display text-2xl">Link {link.status}</h2>
          <p className="text-sm text-slate-400 mt-2">Ask the organiser for a fresh upload link.</p>
        </div>
      )}
    </PublicShell>
  );
};

export default PhotographerUploadPage;
